// DRIVO Concierge — valet operations dashboard.
// Ported from the "Valet Concierge Pro" prototype (Tailwind/shadcn) and
// restyled to the Drivo console design system (see theme.js / DESIGN.md):
// white canvas + near-black ink, action-indigo #3127B8 for primary/action,
// drivo-green for positive state, iOS red/amber for danger/warning.
//
// `T` is the token palette (mirrors the Insights `P` object) — plain hex/rgba
// values so they resolve cleanly in both inline styles and inline SVG.

export const T = {
  bg: "#FFFFFF", // page canvas
  surface: "#F7F8F9", // gray05 — subtle fill / rows
  elevated: "#FFFFFF", // white card surface
  fg: "#1A1A1A", // body ink
  muted: "#EAEDEF", // gray10 — muted fill / track
  mutedFg: "#666666", // gray60 — secondary text
  subtleFg: "#999999", // gray40 — tertiary / eyebrow text
  border: "#D2D6DA", // gray30 — workhorse divider
  borderSoft: "#EAEDEF", // gray10 — hairline

  primary: "#3127B8", // action-indigo
  primaryFg: "#FFFFFF",
  primaryDark: "#241C8A", // hover / pressed
  primaryTint: "rgba(49,39,184,0.10)",

  accent: "#ECEBF8", // light indigo wash — selected state
  accentFg: "#241C8A",

  success: "#3EAC4D", // drivo-green — positive / paid
  successFg: "#FFFFFF",
  successText: "#2E7D32", // readable green text on tint
  successTint: "rgba(62,172,77,0.12)",

  warning: "#B7791F", // amber text
  warningSolid: "#FFC300", // amber fill
  warningFg: "#5C4700", // dark text on amber tint
  warningTint: "rgba(255,195,0,0.18)",

  danger: "#FF3B30", // iOS red
  dangerFg: "#FFFFFF",
  dangerTint: "rgba(255,59,48,0.10)",
};

export const CARD_SHADOW = "0 6px 10px rgba(0,0,0,.08), 0 0 6px rgba(0,0,0,.05)";
export const FONT_SANS =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

export const CAPACITY = 60;
export const STAFF_ROSTER = ["Nick", "Ben", "Felicity", "Andrew", "David", "Sarah"];

export const photoAngles = [
  { angle: "FRONT", label: "Front" },
  { angle: "DRIVER_SIDE", label: "Driver side" },
  { angle: "PASSENGER_SIDE", label: "Passenger side" },
  { angle: "REAR", label: "Rear" },
];

/*
 * Every set of photos lives on the booking: INTAKE is the condition record the
 * concierge shoots at handoff, PARKING is what the runner shoots where the car
 * ends up, HANDOVER is what they shoot at the guest before the keys go back.
 */
export const PHOTO_STAGES = {
  INTAKE: "INTAKE",
  PARKING: "PARKING",
  HANDOVER: "HANDOVER",
};

/* What each set is called where the three are named together, as a line of
   history rather than a screen of its own. */
export const PHOTO_STAGE_LABEL = {
  INTAKE: "Arrival condition",
  PARKING: "Parking handover",
  HANDOVER: "Return condition",
};

/* What the set is called when it is the thing on the screen. */
export const PHOTO_STAGE_TITLE = {
  INTAKE: "Intake photos",
  PARKING: "Parking photos",
  HANDOVER: "Handover photos",
};

const inHours = (h) => new Date(Date.now() + h * 3600000).toISOString();
const agoMin = (m) => new Date(Date.now() - m * 60000).toISOString();

export const initialParked = [
  {
    id: "b-201",
    plate: "BXR 44K",
    guestName: "Nick",
    phone: "+61 412 908 221",
    vip: true,
    checkOut: inHours(1.2),
    paid: true,
    amount: 30,
    vehicle: "Tesla Model S - Pearl White",
    parkedAt: agoMin(190),
    assignedTo: "Ben",
    bay: "F-08",
    status: "parked",
    stacked: false,
  },
  {
    id: "b-205",
    plate: "GRT 118",
    guestName: "Ben",
    phone: "+61 401 552 118",
    vip: false,
    checkOut: inHours(0.3),
    paid: true,
    amount: 30,
    vehicle: "Lexus RX - Sonic Silver",
    parkedAt: agoMin(140),
    assignedTo: "Andrew",
    bay: "F-03",
    status: "parked",
    stacked: false,
  },
  {
    id: "b-198",
    plate: "CJH 021",
    guestName: "Felicity",
    phone: "+61 433 018 774",
    vip: false,
    checkOut: inHours(3),
    paid: true,
    amount: 15,
    vehicle: "BMW X5 - Black Sapphire",
    parkedAt: agoMin(420),
    assignedTo: "Nick",
    bay: "F-11",
    status: "parked",
    stacked: false,
  },
  {
    id: "b-192",
    plate: "AAA 123",
    guestName: "Andrew",
    phone: "+61 402 771 004",
    vip: false,
    checkOut: inHours(-0.7),
    paid: true,
    amount: 15,
    vehicle: "Audi Q3 - Mythos Black",
    parkedAt: agoMin(1300),
    assignedTo: "Ben",
    bay: "R-02",
    status: "overstay",
    stacked: true,
  },
  {
    id: "b-188",
    plate: "TRV 900",
    guestName: "David",
    phone: "+61 419 220 887",
    vip: true,
    checkOut: inHours(28),
    paid: true,
    amount: 45,
    vehicle: "Range Rover Velar - Corris Grey",
    parkedAt: agoMin(2050),
    assignedTo: "Nick",
    bay: "R-14",
    status: "parked",
    stacked: true,
  },
  {
    id: "b-181",
    plate: "LNK 552",
    guestName: "Sarah",
    phone: "+61 400 559 331",
    vip: false,
    checkOut: inHours(-2.4),
    paid: true,
    amount: 15,
    vehicle: "Kia Sportage - Steel Grey",
    parkedAt: agoMin(1780),
    assignedTo: "Ben",
    bay: "R-05",
    status: "expired",
    stacked: true,
  },
  {
    id: "b-175",
    plate: "WLK 019",
    guestName: "David",
    phone: "+61 411 803 219",
    vip: false,
    checkOut: null, // guest hasn't committed to a departure yet
    paid: true,
    amount: 15,
    vehicle: "Toyota Camry - Graphite",
    parkedAt: agoMin(510),
    assignedTo: "Andrew",
    bay: "R-09",
    status: "unknown",
    stacked: true,
  },
];

export const incomingQueue = [
  {
    id: "b-311",
    plate: "MDR 88Q",
    guestName: "Sarah",
    phone: "+61 402 118 774",
    vip: true,
    checkIn: inHours(-0.2),
    checkOut: inHours(72),
    paid: true,
    amount: 45,
    notes: "Anniversary stay. Champagne on arrival.",
    email: "sarah@drivo.com.au",
    bookingId: "b1a7f2c9-4483-4e11-9d82-207c88a91d40",
    guestCard: "Hotel Guest Pre-Book (v2)",
    cardLast4: "5144",
  },
  {
    id: "b-312",
    plate: "KLA 774",
    guestName: "David",
    vip: false,
    checkIn: inHours(-0.1),
    checkOut: inHours(24),
    paid: false,
    amount: 15,
    notes: "Offsite lot - payment must clear before ticket.",
    email: "david@drivo.com.au",
    bookingId: "c3d1e5aa-118e-40a7-9f3c-4a7d21b5e802",
    guestCard: "Walk-up (unpaid)",
  },
  {
    id: "b-313",
    plate: "NEV 220",
    guestName: "Nick",
    vip: false,
    checkIn: inHours(0),
    checkOut: inHours(48),
    paid: true,
    amount: 30,
    email: "nick@drivo.com.au",
    bookingId: "9f2b74c1-3a20-4f88-b4c0-15e903a5c771",
    guestCard: "Hotel Guest Pre-Book (v2)",
    cardLast4: "0921",
  },
  {
    id: "b-314",
    plate: "SGT 402",
    guestName: "Ben",
    phone: "+61 421 776 900",
    vip: false,
    checkIn: inHours(0.1),
    checkOut: inHours(20),
    paid: true,
    amount: 15,
    email: "ben@drivo.com.au",
    bookingId: "8c11a204-77b3-4d51-a91e-2210cc9e4f13",
    guestCard: "Hotel Guest Pre-Book (v2)",
    cardLast4: "3388",
  },
  {
    id: "b-315",
    plate: "OKR 559",
    guestName: "Felicity",
    phone: "+61 439 004 118",
    vip: true,
    checkIn: inHours(0.2),
    checkOut: inHours(46),
    paid: true,
    amount: 30,
    notes: "Prefers key drop at concierge desk.",
    email: "felicity@drivo.com.au",
    bookingId: "44e0f3ca-9c19-4b6d-8b8f-6d13221e77a5",
    guestCard: "VIP Pre-Book",
    cardLast4: "7702",
  },
];

/* ---- time / sort helpers ---- */

export function formatRelative(iso) {
  const diff = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return diff >= 0 ? `in ${s}` : `${s} ago`;
}

export function formatSince(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/* The time of day something happened, which is what a runner reads a row by. */
export function formatClock(iso) {
  if (!iso) return null;

  const at = new Date(iso);

  return Number.isNaN(at.getTime())
    ? null
    : at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/*
 * A booking that has stopped moving. BookingStatus on the API is PENDING_ENTRY,
 * CURRENT, CLOSED, FINALISED, CANCELED, ERROR or DELETED; everything but the
 * first two is terminal there, and nothing on one of them can be amended.
 */
export const TERMINAL_BOOKING_STATUSES = [
  "CLOSED",
  "FINALISED",
  "CANCELED",
  "ERROR",
  "DELETED",
];

export const RUNNER_IN_HAND_STATUSES = [
  "EN_ROUTE",
  "VEHICLE_LOCATED",
  "RETURNING",
  "READY",
];

/*
 * A pickup one runner has taken, from accepting it to standing at the door with
 * it. The runner queue shows every one of them; the row works out which tap
 * comes next from the status.
 */
export const PICKUP_IN_PROGRESS_STATUSES = [
  "RUNNER_ASSIGNED",
  ...RUNNER_IN_HAND_STATUSES,
];

/*
 * The taps a runner makes between accepting a pickup and handing the keys over,
 * in the order they happen. `action` is the button; `mutation` is the write.
 */
export const PICKUP_STEPS = [
  {
    from: "RUNNER_ASSIGNED",
    action: "Take keys & start collection",
    mutation: "startEnRoute",
    done: "keys taken",
  },
  {
    from: "EN_ROUTE",
    action: "Car collected",
    mutation: "markVehicleLocated",
    done: "car collected",
  },
  {
    from: "VEHICLE_LOCATED",
    action: "En route to guest",
    mutation: "markCollected",
    done: "on the way to the guest",
  },
  {
    from: "RETURNING",
    action: "Ready at porte-cochere",
    mutation: "markReady",
    done: "ready at the door",
  },
];

export function nextStepFor(status) {
  return PICKUP_STEPS.find((step) => step.from === status) ?? null;
}

/*
 * The six stages a guest's pickup passes through, as the booking view draws
 * them. Coarser than PICKUP_STEPS: several statuses can share one stage, and
 * the last one is reached after the runner's work is done.
 */
export const PICKUP_WORKFLOW = [
  { key: "ASSIGNED", label: "Assigned", statuses: ["REQUESTED", "RUNNER_ASSIGNED"] },
  { key: "EN_ROUTE_CAR", label: "En route to collect car", statuses: ["EN_ROUTE"] },
  { key: "COLLECTED", label: "Car collected", statuses: ["VEHICLE_LOCATED"] },
  { key: "EN_ROUTE_GUEST", label: "En route to guest", statuses: ["RETURNING"] },
  { key: "READY", label: "Ready at porte-cochere", statuses: ["READY"] },
  { key: "HANDED", label: "Handed to guest", statuses: ["RETURNED"] },
];

/* What the queue calls each status, in the guest's words rather than the enum's. */
export const OPERATIONAL_STATUS = {
  CLAIMED: "Arrived",
  PARKING: "Being parked",
  PARKED: "Parked",
  REQUESTED: "Pickup requested",
  RUNNER_ASSIGNED: "Pickup requested",
  EN_ROUTE: "Collecting vehicle",
  VEHICLE_LOCATED: "Collecting vehicle",
  RETURNING: "On its way",
  READY: "At porte-cochere",
};

export const SESSIONS_POLL_INTERVAL_MS = 15000;
export const REFRESH_INTERVAL_MS = 60000;
